import React, { useEffect, useState } from "react";
import { OrganizationEvents } from "@app/shared/src/api/api.gen";
import { EventReviewRequest, FacilityEvent, IEventReviewRequest } from "@app/shared/src/models";
import { Btn, Modal, OptionButton, useToast } from "@app/shared/src/components_v2";
import { isReviewed } from "../../helpers/eventDisplay";

/**
 * Recording what a director concluded about an alert.
 *
 * Two things get captured: whether it was real, and why they think so. The verdict is the part the
 * event list acts on — dismissed alerts drop out of the default view — so it is asked as a plain
 * choice rather than a checkbox someone can leave in an ambiguous middle state.
 *
 * Reviews are revisable. Opening this on an already-reviewed event seeds the existing verdict and
 * notes, and "Clear review" puts the event back to untouched, because the first call is sometimes
 * made on a phone at night from a thumbnail.
 */
export const ReviewEventDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    event: FacilityEvent;
    orgId?: string;
    onReviewed: (updated: FacilityEvent) => void;
}> = ({ open, onClose, event, orgId, onReviewed }) => {
    const toast = useToast();
    const [valid, setValid] = useState<boolean | undefined>(event.isValidAlert);
    const [notes, setNotes] = useState(event.reviewNotes ?? "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    // Re-seed each time it opens: the event may have been re-reviewed elsewhere in the meantime.
    useEffect(() => {
        if (!open) return;
        setValid(event.isValidAlert);
        setNotes(event.reviewNotes ?? "");
        setError(undefined);
    }, [open, event.isValidAlert, event.reviewNotes]);

    const submit = async (payload: { isValidAlert?: boolean; notes?: string }, done: string) => {
        if (!orgId || !event.id || busy) return;
        setBusy(true);
        setError(undefined);
        try {
            const updated = await OrganizationEvents.reviewEvent(
                orgId,
                event.id,
                new EventReviewRequest(payload as unknown as IEventReviewRequest),
            );
            if (updated) onReviewed(updated);
            toast(done);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save that review");
        } finally {
            setBusy(false);
        }
    };

    const alreadyReviewed = isReviewed(event);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={alreadyReviewed ? "Update this review" : "Mark reviewed"}
            subtitle="Your assessment travels with the event — on the list, in the CSV, and in the incident report."
            footer={
                <>
                    {alreadyReviewed && (
                        <Btn
                            tone="quiet"
                            disabled={busy}
                            onClick={() => submit({ isValidAlert: undefined, notes: undefined }, "Review cleared")}
                        >
                            Clear review
                        </Btn>
                    )}
                    <Btn tone="quiet" onClick={onClose} disabled={busy}>
                        Cancel
                    </Btn>
                    <Btn onClick={() => submit({ isValidAlert: valid, notes }, "Marked reviewed")} disabled={busy || valid === undefined}>
                        {busy ? "Saving…" : "Save review"}
                    </Btn>
                </>
            }
        >
            <div>
                <label className="text-[12px] text-[var(--muted)]">Was this a valid alert?</label>
                <div className="mt-1.5 flex gap-2">
                    <OptionButton selected={valid === true} onClick={() => setValid(true)}>
                        Valid alert
                    </OptionButton>
                    <OptionButton selected={valid === false} onClick={() => setValid(false)}>
                        Not a real alert
                    </OptionButton>
                </div>
                <p className="mt-1.5 text-[11.5px] leading-relaxed text-[var(--faint)]">
                    Alerts marked as not real are hidden from the events list unless "Show invalid" is ticked.
                </p>
            </div>

            <div>
                <label className="text-[12px] text-[var(--muted)]">Notes</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="What did you see in the clip? Anything the crew should know?"
                    className="mt-1.5 w-full resize-y rounded-[12px] border border-[var(--line-2)] bg-[var(--surface-2)] px-3.5 py-2.5 text-[13.5px] text-[var(--text)] outline-none placeholder:text-[var(--faint)] focus:border-[var(--accent)]"
                />
            </div>

            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
